import { useState, useEffect, useCallback } from 'react';
import { invoicesAPI, clientsAPI, servicesAPI } from '../services/api';
import { useToast } from './use-toast';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';

export const useInvoices = () => {
  const { toast } = useToast();
  
  // Core data
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters and sorting
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('invoiceDate');
  const [sortOrder, setSortOrder] = useState('desc');

  // Fetch functions
  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = { sort_by: sortBy, sort_order: sortOrder };
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await invoicesAPI.getAll(params);
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast({ title: 'Error', description: 'Failed to load invoices', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sortBy, sortOrder, toast]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await clientsAPI.getAll({});
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
    fetchClients();
    fetchServices();
  }, [fetchInvoices, fetchClients, fetchServices]);

  // Sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Invoice operations
  const handleTogglePaid = async (invoice, openPaymentDialog) => {
    if (invoice.status === 'paid') {
      try {
        await invoicesAPI.unpay(invoice._id);
        toast({ title: 'Success', description: 'Invoice marked as unpaid' });
        fetchInvoices();
      } catch (error) {
        console.error('Error updating invoice:', error);
        toast({ title: 'Error', description: 'Failed to update invoice', variant: 'destructive' });
      }
    } else {
      openPaymentDialog(invoice);
    }
  };

  const handleConfirmPayment = async (invoiceToPay, paymentMode, paidDate, paymentMemo, paymentAmount, onSuccess) => {
    if (!paymentMode) {
      toast({ title: 'Error', description: 'Please select a payment mode', variant: 'destructive' });
      return false;
    }
    try {
      await invoicesAPI.pay(invoiceToPay._id, paymentMode, paidDate, paymentMemo, paymentAmount);
      toast({ title: 'Success', description: `Payment of $${paymentAmount?.toFixed(2) || '0.00'} recorded` });
      fetchInvoices();
      onSuccess();
      return true;
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast({ title: 'Error', description: 'Failed to record payment', variant: 'destructive' });
      return false;
    }
  };

  const handleDeleteInvoice = async (invoice, onSuccess) => {
    if (!invoice) return;
    try {
      await invoicesAPI.delete(invoice._id);
      toast({ title: 'Success', description: `Invoice ${invoice.invoiceNumber} deleted` });
      fetchInvoices();
      onSuccess();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast({ title: 'Error', description: 'Failed to delete invoice', variant: 'destructive' });
    }
  };

  // Service operations
  const handleCreateService = async (newService, onSuccess) => {
    if (!newService.name) {
      toast({ title: 'Error', description: 'Service name is required', variant: 'destructive' });
      return false;
    }
    try {
      await servicesAPI.create(newService);
      toast({ title: 'Success', description: 'Service created' });
      fetchServices();
      onSuccess();
      return true;
    } catch (error) {
      console.error('Error creating service:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create service', variant: 'destructive' });
      return false;
    }
  };

  const handleUpdateService = async (service, onSuccess) => {
    if (!service?.name) {
      toast({ title: 'Error', description: 'Service name is required', variant: 'destructive' });
      return false;
    }
    try {
      await servicesAPI.update(service._id, {
        name: service.name,
        description: service.description,
        defaultAmount: service.defaultAmount
      });
      toast({ title: 'Success', description: 'Service updated' });
      fetchServices();
      onSuccess();
      return true;
    } catch (error) {
      console.error('Error updating service:', error);
      toast({ title: 'Error', description: 'Failed to update service', variant: 'destructive' });
      return false;
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await servicesAPI.delete(serviceId);
      toast({ title: 'Success', description: 'Service deleted' });
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast({ title: 'Error', description: 'Failed to delete service', variant: 'destructive' });
    }
  };

  // Excel export
  const exportToExcel = () => {
    if (invoices.length === 0) {
      toast({ title: 'No Data', description: 'No invoices to export', variant: 'destructive' });
      return;
    }

    // Keep monetary values as numbers for Excel calculations
    const data = invoices.map(invoice => {
      const total = invoice.grandTotal || 0;
      // For paid invoices without paidAmount (legacy data), use grandTotal as paid amount
      const paidAmount = invoice.status === 'paid' && !invoice.paidAmount 
        ? total 
        : (invoice.paidAmount || 0);
      const balanceDue = total - paidAmount;
      
      return {
        'Invoice #': invoice.invoiceNumber,
        'Client': invoice.clientName,
        'Invoice Date': invoice.invoiceDate ? format(new Date(invoice.invoiceDate), 'MMM dd, yyyy') : '',
        'Due Date': invoice.dueDate ? format(new Date(invoice.dueDate), 'MMM dd, yyyy') : '',
        'Subtotal': invoice.subtotal || 0,
        'HST': invoice.hstAmount || 0,
        'Total': total,
        'Paid Amount': paidAmount,
        'Balance Due': balanceDue,
        'Status': invoice.status,
        'Payment Mode': invoice.paymentMode || '',
        'Paid Date': invoice.paidDate ? format(new Date(invoice.paidDate), 'MMM dd, yyyy') : ''
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
    
    // Set column widths
    ws['!cols'] = [
      { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 15 },
      { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 },
      { wch: 12 }, { wch: 10 }, { wch: 15 }, { wch: 15 }
    ];
    
    // Format numeric columns with currency format (columns E, F, G, H, I)
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = 1; R <= range.e.r; ++R) {
      ['E', 'F', 'G', 'H', 'I'].forEach(col => {
        const cell = ws[col + (R + 1)];
        if (cell && typeof cell.v === 'number') {
          cell.z = '$#,##0.00';
        }
      });
    }
    
    XLSX.writeFile(wb, `Invoices_Export_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    toast({ title: 'Success', description: `Exported ${invoices.length} invoices to Excel` });
  };

  return {
    // Data
    invoices,
    clients,
    services,
    loading,
    
    // Filters & Sorting
    statusFilter,
    setStatusFilter,
    sortBy,
    sortOrder,
    handleSort,
    
    // Operations
    fetchInvoices,
    fetchServices,
    handleTogglePaid,
    handleConfirmPayment,
    handleDeleteInvoice,
    handleCreateService,
    handleUpdateService,
    handleDeleteService,
    exportToExcel
  };
};
